import { Component, Fragment } from 'react';
import { Card, Text, Stack, Button } from '@servicetitan/design-system';
import classNames from 'classnames';
import { ACHModal } from './ach-modal';
import { RemoveACHModal } from './ach-remove-modal';
import { BankAccountModel } from '../api/internal-billing.api';
import { ACHLearnMoreModal } from './ach-learn-more-modal';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { provide } from '@servicetitan/react-ioc';
import { AchModalStore, PlaidIntegrations } from '..';

interface Props {
    removeBankHandler(): void;
    onComplete(bankDetails: BankAccountModel): void;
    isLoading?: boolean;
    isPendingVerification?: boolean;
    isAutoVerification?: boolean;
    disableManualVerification?: boolean;
    cancelButtonText?: string;
    header?: string;
    additionalVerificationText: string;
}

@provide({ singletons: [AchModalStore, PlaidIntegrations] })
@observer
export class ConnectBankACHCard extends Component<Props> {
    @observable
    shouldShowLearnMoreModal = false;

    constructor(props: Props) {
        super(props);
        makeObservable(this);
    }

    @action
    openLearnMoreModal = () => (this.shouldShowLearnMoreModal = true);

    @action
    closeLearnMoreModal = () => (this.shouldShowLearnMoreModal = false);

    render() {
        let header = this.props.header || 'Introducing ACH Payments';
        let content = (
            <Fragment>
                <span>
                    Securely connect to your bank. Your bank credentials will not be shared with
                    ServiceTitan. You can also choose to enter your account number instead.
                </span>
                &nbsp;
                <span>
                    <Button primary text onClick={this.openLearnMoreModal} className="p-0-i fs-3">
                        Learn More
                    </Button>
                </span>
            </Fragment>
        );
        let buttonText = 'Connect Your Bank';

        if (this.props.isPendingVerification) {
            header = this.props.isAutoVerification
                ? 'Pending Auto Verification'
                : 'Additional Verification Required';
            content = (
                <span>
                    {this.props.isAutoVerification
                        ? `We've made two small deposits to your bank account that will show within 24 hours.
                            Once it's posted, we'll auto-confirm the deposits and finalize your ACH setup.`
                        : this.props.additionalVerificationText}
                </span>
            );
            buttonText = 'Verify Deposits';
        }

        const showActionButton =
            !this.props.isPendingVerification ||
            (this.props.isPendingVerification &&
                !this.props.isAutoVerification &&
                !this.props.disableManualVerification);

        return (
            <Fragment>
                {this.shouldShowLearnMoreModal && (
                    <ACHLearnMoreModal onClose={this.closeLearnMoreModal} />
                )}
                <Card raised>
                    <Card.Section className="p-x-5">
                        <div className={classNames('p-x-6', 'p-b-4', 'p-t-1')}>
                            <img
                                src={require('../assets/ach-bank-graphic.png')}
                                className="w-100"
                            />
                        </div>
                        <div className="ta-center p-b-2">
                            <Text headline size={3}>
                                {header}
                            </Text>
                        </div>
                        <div className="ta-center p-b-2">
                            <Text body subdued size={3}>
                                {content}
                            </Text>
                        </div>
                        <Stack alignItems="center" className="m-x-auto">
                            {this.props.isPendingVerification && (
                                <Stack.Item fill={!showActionButton}>
                                    <RemoveACHModal
                                        buttonText={this.props.cancelButtonText}
                                        simple={this.props.disableManualVerification}
                                        onRemoveACH={this.props.removeBankHandler}
                                    />
                                </Stack.Item>
                            )}
                            {showActionButton && (
                                <Stack.Item fill>
                                    <div className="m-y-half m-x-auto p-y-1">
                                        <ACHModal
                                            buttonText={buttonText}
                                            onComplete={this.props.onComplete}
                                            isPendingVerification={this.props.isPendingVerification}
                                        />
                                    </div>
                                </Stack.Item>
                            )}
                        </Stack>
                    </Card.Section>
                </Card>
            </Fragment>
        );
    }
}
